import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Party } from "@/app/types/party";
import { PaginatedResponse } from "@/app/types/api";

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

interface PartyDataTableProps {
  data: PaginatedResponse<Party>;
  columns: ExtendedColumnDef<Party>[];
  isLoading: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
    pageCount: number;
  };
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onSortingChange: (sorting: SortingState) => void;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
}

const PartyDataTable: React.FC<PartyDataTableProps> = ({
  data,
  columns,
  isLoading,
  pagination,
  sorting,
  columnFilters,
  onPaginationChange,
  onSortingChange,
  onColumnFiltersChange,
}) => {
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    onSortingChange(newSorting);
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };

  const handleColumnFiltersChange = (updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    onColumnFiltersChange(newFilters);
    // Reset to first page when filters change
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };

  // Create the table instance
  const table = useReactTable({
    data: data.items,
    columns: columns as ColumnDef<Party>[], // Cast back for useReactTable
    pageCount: Math.ceil(data.total / pagination.pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: onPaginationChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const getColumnKey = (column: ExtendedColumnDef<Party>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };

  const renderFilterControl = (column: ExtendedColumnDef<Party>): JSX.Element | null => {
    const columnKey = getColumnKey(column);
    const tableColumn = table.getColumn(columnKey);
    const columnMeta = column.meta;

    if (!tableColumn || !column.enableColumnFilter || !columnMeta?.variant) {
      return null;
    }

    const filterValue = tableColumn.getFilterValue();

    if (columnMeta.variant === 'text' || columnMeta.variant === 'number') {
      return (
        <Input
          type={columnMeta.variant === 'number' ? 'number' : 'text'}
          value={(filterValue ?? '') as string}
          onChange={(e) => tableColumn.setFilterValue(e.target.value)}
          placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
          className="h-8 w-full max-w-xs"
        />
      );
    }

    if (columnMeta.variant === 'select' && columnMeta.options) {
      const ALL_VALUE = "__ALL__"; // Special value for the "All" option
      return (
        <Select
          value={(filterValue ?? ALL_VALUE) as string} // Default to ALL_VALUE if filter is undefined
          onValueChange={value =>
            tableColumn.setFilterValue(value === ALL_VALUE ? undefined : value)
          }
        >
          <SelectTrigger className="h-8 w-full max-w-xs">
            <SelectValue placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`} />
          </SelectTrigger>
          <SelectContent>
            {/* Use the special value for the "All" item */}
            <SelectItem value={ALL_VALUE}>All</SelectItem>
            {columnMeta.options.map((option) => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Column Visibility Controls */}
      <div className="flex items-center justify-end gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((column) => {
              const columnKey = getColumnKey(column);
              const tableCol = table.getColumn(columnKey);
              // Ensure tableCol exists and getCanHide is callable
              return tableCol?.getCanHide() && (
                <DropdownMenuCheckboxItem
                  key={columnKey}
                  className="capitalize"
                  checked={tableCol.getIsVisible()}
                  onCheckedChange={(value) =>
                    tableCol.toggleVisibility(!!value)
                  }
                >
                  {column.meta?.label || columnKey}
                </DropdownMenuCheckboxItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row for Individual Column Filters */}
      <div className="flex items-center gap-2 flex-wrap p-2 border rounded-md bg-card">
        {columns.map((column) => (
            column.enableColumnFilter && (
                <div key={getColumnKey(column)} className="flex flex-col space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">
                        {column.meta?.label || getColumnKey(column)}
                    </label>
                    {renderFilterControl(column)}
                </div>
            )
        ))}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={(e) => {
                    // Only navigate if the click wasn't on an action button or dropdown
                    const target = e.target as HTMLElement;
                    const isActionClick = target.closest('[role="button"]') ||
                                        target.closest('button') ||
                                        target.closest('[data-radix-collection-item]');
                    if (!isActionClick) {
                      const party = row.original;
                      const path = `/party/detail/${party.id}`;
                      navigate(path);
                    }
                  }}
                  title="Click to view party details"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No parties assigned to you.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between w-full">
        <div className="text-sm text-muted-foreground">
          Page {pagination.pageIndex + 1} of {pagination.pageCount || 1}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
            disabled={pagination.pageIndex === 0 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
            disabled={pagination.pageIndex + 1 >= pagination.pageCount || isLoading}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PartyDataTable;
